// File: src/components/constants/observable-constants.ts
import {KeyValueObservable} from '../../util/observers/KeyValueObservable';
import {SECONDS_IN_A_MINUTE, MILLISECONDS_IN_A_SECOND} from './time-constants';
import {IAppContext} from "../../interfaces";
import {observableEndpoints} from "../../requests/api-endpoints";
import {isAdminAccount, isBuyerAccount, isSupplierAccount, hasOwnerAccess, hasGateManagerAccess} from "../../util/AccountUtils";
import {getNumericRequest, getRequestCountVo, getBuyerBookingCounts} from "../../requests/generic-requests";
import type { Account } from '../../types/Account';

export type ObservablePayloadType = string | number | boolean;

export interface IObservableVariable {
  key: string,
  default: ObservablePayloadType,
  pollingInterval: (context: IAppContext) => number | null,
  refreshData: (context: IAppContext) => void
}

interface IObservableVariablePairs {
  [key: string]: IObservableVariable;
}

//observableEndpoints is structured by account type, then by endpoint name, so we need to
//determine the user's account type before we can get the correct endpoint for the observable variable
const getUserTypedEndpoint = <T extends string>(user: Account, endpoint: T) => {
  let url: string | undefined;
  if (isSupplierAccount(user)) {
    url = (observableEndpoints.supplier as Record<string, string>)[endpoint];
  } else if (isBuyerAccount(user)) {
    url = (observableEndpoints.buyer as Record<string, string>)[endpoint];
  } else if (isAdminAccount(user)) {
    url = (observableEndpoints.admin as Record<string, string>)[endpoint];
  }
  return url || null;
}

const nullPollingInterval = (_context: IAppContext) => null;

export const ObservableVariables: IObservableVariablePairs = Object.freeze({
  PENDING_APPROVAL_COUNT: {
    key: "pendingApprovalCount",
    default: 0,
    pollingInterval: (context: IAppContext) =>
    {
      const { user } = context;
      return isSupplierAccount(user) ? (SECONDS_IN_A_MINUTE * MILLISECONDS_IN_A_SECOND * 2) /*2 minutes*/ : null
    },
    refreshData: (context: IAppContext) => {
      const { user, observableMap } = context;
      if (!user || !observableMap) {
        return;
      }
      if (isBuyerAccount(user)) {
        // Buyer pending/failed booking counts are owner-scoped.
        if (!hasOwnerAccess(user)) {
          return;
        }
        const endpoint = observableEndpoints.buyer.buyerBookingCounts;
        if (endpoint) {
          getBuyerBookingCounts(endpoint)
            .then((counts) => {
              observableMap.set(ObservableVariables.PENDING_APPROVAL_COUNT.key, counts.pendingCount);
              observableMap.set(ObservableVariables.FAILED_BOOKING_COUNT.key, counts.failedCount);
            })
            .catch(() => {
              observableMap.set(ObservableVariables.PENDING_APPROVAL_COUNT.key, 0);
              observableMap.set(ObservableVariables.FAILED_BOOKING_COUNT.key, 0);
            });
        }
      } else if (isSupplierAccount(user)) {
        // Supplier gate managers (and owners/admins) have the Approvals menu, so
        // gate-manager-level access — not owner-only — gates the approval count.
        if (!hasGateManagerAccess(user)) {
          return;
        }
        const endpoint = getUserTypedEndpoint(user, "pendingBookingsCount");
        if (endpoint) {
          getNumericRequest(endpoint)
            .then((value: number) => {
              observableMap.set(ObservableVariables.PENDING_APPROVAL_COUNT.key, value);
            })
            .catch(() => {
              observableMap.set(ObservableVariables.PENDING_APPROVAL_COUNT.key, 0);
            });
        }
      }
    }
  },
  FAILED_BOOKING_COUNT: {
    key: "failedBookingCount",
    default: 0,
    pollingInterval: nullPollingInterval,
    refreshData: (context: IAppContext) => {
      // failedCount is refreshed alongside pendingCount in PENDING_APPROVAL_COUNT
      // to avoid duplicate API calls to buyer-booking-counts
      ObservableVariables.PENDING_APPROVAL_COUNT.refreshData(context);
    }
  },
  PENDING_INVENTORY_CORRECTIONS_COUNT: {
    key: "pendingInventoryCorrectionsCount",
    default: 0,
    pollingInterval: nullPollingInterval,
    refreshData: (context: IAppContext) => {
      const { user, observableMap } = context;
      if (user && observableMap) {
        const endpoint = getUserTypedEndpoint(user, "inventoryCorrectionsCount");
        if (endpoint) {
          getNumericRequest(endpoint)
            .then((value: number) => {
              observableMap.set(ObservableVariables.PENDING_INVENTORY_CORRECTIONS_COUNT.key, value);
            })
            .catch(() => {
              observableMap.set(ObservableVariables.PENDING_INVENTORY_CORRECTIONS_COUNT.key, 0);
            });
        }
      }
    }
  },
  PENDING_INVENTORY_ACTIVITY_COUNT: {
    key: "pendingInventoryActivityCount",
    default: 0,
    pollingInterval: nullPollingInterval,
    refreshData: (context: IAppContext) => {
      const { user, observableMap } = context;
      if (user && observableMap && hasOwnerAccess(user) && isSupplierAccount(user)) {
        getRequestCountVo(observableEndpoints.supplier.pendingInventoryActivityCount)
          .then((value: number) => {
            observableMap.set(ObservableVariables.PENDING_INVENTORY_ACTIVITY_COUNT.key, value);
          })
          .catch(() => {
            observableMap.set(ObservableVariables.PENDING_INVENTORY_ACTIVITY_COUNT.key, 0);
          });
      }
    }
  },
});

export const seedObservableMap: (observableKeyValuePairs: KeyValueObservable<ObservablePayloadType> | undefined) => KeyValueObservable<ObservablePayloadType> = (observableKeyValuePairs: KeyValueObservable<ObservablePayloadType> | undefined) => {
  if (!observableKeyValuePairs) {
    observableKeyValuePairs = new KeyValueObservable<ObservablePayloadType>();
  }

  let key: string;
  for (key in ObservableVariables) {
    observableKeyValuePairs.set(ObservableVariables[key].key, ObservableVariables[key].default);
  }
  return observableKeyValuePairs;
}
